import type { MarketQuote } from '@/types';

// ---------------------------------------------------------------------------
// Market Data Service
// CoinGecko public API (no key required) for crypto prices.
// ---------------------------------------------------------------------------

// Dev: Vite 미들웨어, Prod: Vercel 서버리스 — 동일 경로 /api/crypto
const COINGECKO_SIMPLE_URL = '/api/crypto';
const TIMEOUT_MS = 12_000;

/** Crypto assets to track */
const CRYPTO_IDS = [
  { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin' },
  { id: 'ethereum', symbol: 'ETH', name: 'Ethereum' },
  { id: 'solana', symbol: 'SOL', name: 'Solana' },
  { id: 'ripple', symbol: 'XRP', name: 'XRP' },
  { id: 'tether', symbol: 'USDT', name: 'Tether' },
  { id: 'cardano', symbol: 'ADA', name: 'Cardano' },
  { id: 'dogecoin', symbol: 'DOGE', name: 'Dogecoin' },
  { id: 'polkadot', symbol: 'DOT', name: 'Polkadot' },
] as const;

interface CoinGeckoPrice {
  usd: number;
  usd_24h_change?: number;
}

type CoinGeckoResponse = Record<string, CoinGeckoPrice>;

/**
 * Fetch crypto prices from CoinGecko public API.
 * No API key required for the simple/price endpoint.
 */
async function fetchCryptoPrices(): Promise<MarketQuote[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  // /api/crypto 서버가 전체 파라미터 포함해서 fetch
  const url = COINGECKO_SIMPLE_URL;

  try {
    const res = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) {
      throw new Error(`CoinGecko responded with ${res.status}`);
    }

    const data: CoinGeckoResponse = await res.json();
    const now = new Date().toISOString();

    return CRYPTO_IDS
      .filter((c) => data[c.id]?.usd != null)
      .map((c): MarketQuote => {
        const entry = data[c.id];
        const price = entry.usd;
        const changePercent = entry.usd_24h_change ?? 0;
        const change = price * (changePercent / 100);

        return {
          symbol: c.symbol,
          name: c.name,
          price,
          change: Math.round(change * 100) / 100,
          changePercent: Math.round(changePercent * 100) / 100,
          timestamp: now,
          category: 'crypto' as const,
          unit: 'USD',
        };
      });
  } catch (err) {
    console.error('[Market] CoinGecko fetch failed', err);
    return [];
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Major Index Proxies via free public APIs
// ---------------------------------------------------------------------------

/** Fear & Greed index as a sentiment proxy (alternative.me free API) */
const FEAR_GREED_URL = 'https://api.alternative.me/fng/?limit=1';

interface FearGreedResponse {
  data: { value: string; value_classification: string; timestamp: string }[];
}

async function fetchFearGreedIndex(): Promise<MarketQuote | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8_000);

  try {
    const res = await fetch(FEAR_GREED_URL, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) return null;

    const data: FearGreedResponse = await res.json();
    const entry = data.data?.[0];
    if (!entry) return null;

    const value = Number(entry.value);

    return {
      symbol: 'FNG',
      name: `Fear & Greed (${entry.value_classification})`,
      price: value,
      change: 0,
      changePercent: 0,
      timestamp: new Date(Number(entry.timestamp) * 1000).toISOString(),
      category: 'macro' as const,
      unit: 'index',
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Gold price via free public endpoint
// ---------------------------------------------------------------------------

// Gold은 /api/crypto 응답의 pax-gold 항목 사용 (dev/prod 동일)
const GOLD_API_URL = '/api/crypto';

async function fetchGoldProxy(): Promise<MarketQuote | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8_000);

  try {
    const res = await fetch(GOLD_API_URL, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) return null;

    const data: CoinGeckoResponse = await res.json();
    const paxg = data['pax-gold'];
    if (!paxg?.usd) return null;

    const changePercent = paxg.usd_24h_change ?? 0;
    const change = paxg.usd * (changePercent / 100);

    return {
      symbol: 'GOLD',
      name: 'Gold (PAXG proxy)',
      price: Math.round(paxg.usd * 100) / 100,
      change: Math.round(change * 100) / 100,
      changePercent: Math.round(changePercent * 100) / 100,
      timestamp: new Date().toISOString(),
      category: 'commodity' as const,
      unit: '$/oz',
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Public API — aggregates all market data
// ---------------------------------------------------------------------------

/**
 * Fetch all market data: crypto prices + sentiment index + gold proxy.
 * Returns a combined array of MarketQuote records.
 */
export async function fetchMarketData(): Promise<MarketQuote[]> {
  const [cryptoPrices, fearGreed, gold] = await Promise.allSettled([
    fetchCryptoPrices(),
    fetchFearGreedIndex(),
    fetchGoldProxy(),
  ]);

  const quotes: MarketQuote[] = [];

  // Crypto prices
  if (cryptoPrices.status === 'fulfilled') {
    quotes.push(...cryptoPrices.value);
  }

  // Gold proxy
  if (gold.status === 'fulfilled' && gold.value) {
    quotes.push(gold.value);
  }

  // Fear & Greed index
  if (fearGreed.status === 'fulfilled' && fearGreed.value) {
    quotes.push(fearGreed.value);
  }

  return quotes;
}
